import React, { useState, useEffect } from 'react'

const LobbyScreen = ({ 
  player, 
  lobbyPlayers, 
  onMatchUser, 
  onLeaveLobby, 
  onShareWithFriends, 
  onDownloadKey, 
  onGoToAnalytics, 
  onGoToAbout 
}) => {
  const [showHowItWorks, setShowHowItWorks] = useState(false)
  const [showMenu, setShowMenu] = useState(false)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="card-retro p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neon-green mb-2">
                Welcome to Purgatory
              </h1>
              <p className="text-purgatory-mist">
                Escape Purgatory by finding common ground with another soul.
              </p>
            </div>
            
            {/* Menu Button */}
            <div className="relative menu-container">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-purgatory-mist hover:text-neon-green transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-purgatory-dark border border-purgatory-gold rounded shadow-lg z-50">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onDownloadKey()
                        setShowMenu(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-purgatory-light hover:bg-purgatory-ash hover:bg-opacity-20"
                    >
                      Download Key
                    </button>
                    <button
                      onClick={() => {
                        onShareWithFriends()
                        setShowMenu(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-purgatory-light hover:bg-purgatory-ash hover:bg-opacity-20"
                    >
                      Share with Friends
                    </button>
                    <button
                      onClick={() => {
                        onGoToAnalytics()
                        setShowMenu(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-purgatory-light hover:bg-purgatory-ash hover:bg-opacity-20"
                    >
                      Data & Analytics
                    </button>
                    <button
                      onClick={() => {
                        onLeaveLobby()
                        setShowMenu(false)
                      }}
                      className="block w-full text-left px-4 py-2 text-purgatory-ember hover:bg-purgatory-ash hover:bg-opacity-20"
                    >
                      Leave Purgatory
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Your Soul Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card-retro p-4 text-center">
            <div className="text-2xl font-bold text-neon-green">{player.identity}</div>
            <div className="text-sm text-purgatory-mist">Your Identity</div>
          </div>
          <div className="card-retro p-4 text-center">
            <div className="text-2xl font-bold text-neon-blue">{player.soulsHelped || 0}</div>
            <div className="text-sm text-purgatory-mist">Souls Helped</div>
          </div>
        </div>

        {/* Souls Ready to Escape */}
        <div className="card-retro p-6 mb-6">
          <h2 className="text-xl font-bold text-purgatory-gold mb-4">
            Souls ready to escape
          </h2>
          
          {lobbyPlayers.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">👻</div>
              <p className="text-purgatory-mist mb-4">
                No other souls in purgatory right now.
              </p>
              <button
                onClick={onShareWithFriends}
                className="btn-primary"
              >
                Share with Friends
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lobbyPlayers.map((lobbyPlayer) => (
                <div key={lobbyPlayer.user_id} className="card-retro p-4 text-center">
                  <div className="text-3xl mb-2">{lobbyPlayer.identity}</div>
                  <div className="text-sm text-purgatory-mist mb-3">
                    {lobbyPlayer.souls_helped || 0} souls helped
                  </div>
                  <button
                    onClick={() => onMatchUser(lobbyPlayer.user_id)}
                    className="btn-primary w-full"
                  >
                    Play
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How it works - Collapsible */}
        <div className="card-retro p-6 mb-6">
          <button
            onClick={() => setShowHowItWorks(!showHowItWorks)}
            className="w-full text-left flex items-center justify-between"
          >
            <h3 className="text-lg font-bold text-purgatory-gold">
              How this works
            </h3>
            <div className="text-purgatory-gold text-xl transform transition-transform duration-200">
              {showHowItWorks ? '−' : '+'}
            </div>
          </button>
          
          {showHowItWorks && (
            <div className="mt-4 space-y-4">
              <div>
                <h4 className="text-purgatory-gold font-bold mb-2">🎯 The Goal</h4>
                <p className="text-purgatory-mist text-sm">
                  Find another soul with similar values and escape purgatory together by answering questions that reveal your common ground.
                </p>
              </div>
              
              <div>
                <h4 className="text-purgatory-gold font-bold mb-2">🔄 The Process</h4>
                <p className="text-purgatory-mist text-sm">
                  Click "Play" on a soul you want to match with → Answer 3 questions about your values and beliefs → Discover your common ground percentage → If you both agree, escape together!
                </p>
              </div>
              
              <div>
                <h4 className="text-purgatory-gold font-bold mb-2">💡 Tips</h4>
                <p className="text-purgatory-mist text-sm">
                  Focus on finding at least one matching answer per round. The chat is your reward - use it to discover deeper connections. Stay anonymous and explore what you have in common.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Learn More */}
        <div className="text-center">
          <button
            onClick={onGoToAbout}
            className="btn-secondary"
          >
            Learn More
          </button>
        </div>
      </div>
    </div>
  )
}

export default LobbyScreen
